import { useEffect, useMemo, useRef } from 'react';
import type { AgentThreadListEntry } from '@reflex/ui/components/agent/AgentThreadList';
import type { LaunchedAgentCardItem, LaunchedAgentStatus } from './launched-agent.js';

/**
 * Map a launched-agent card onto the shared `AgentThreadList` row, so the
 * composer's activity bar lists MCP-launched agents alongside the thread's
 * stream sub-agents. The entry — a `@reflex/ui` type — is the shape that
 * crosses to the host; the UI-only {@link LaunchedAgentCardItem} stays inside
 * plugin-chat (see the contract warning in `launched-agent.ts`).
 */

const STATUS_MARKS: Record<LaunchedAgentStatus, AgentThreadListEntry['mark']> = {
  working: undefined,
  needs_input: 'blocked',
  completed: 'completed',
  errored: 'failed',
  // A deliberate stop is not a failure: the chat card renders it as its own
  // neutral state, and the stream-derived entries give `stopped` no mark
  // either, so the merged list stays consistent. The row's headline still
  // says "Stopped".
  stopped: undefined,
};

const STATUS_HEADLINES: Record<LaunchedAgentStatus, string> = {
  working: 'Working',
  needs_input: 'Waiting for input',
  completed: 'Finished',
  errored: 'Failed',
  stopped: 'Stopped',
};

function headlineOf(item: LaunchedAgentCardItem): string {
  // A blocked agent's question is what the user must answer, so it outranks
  // a summary left over from the same result payload — the same priority the
  // chat card renders.
  if (item.status === 'needs_input' && item.question) return item.question;
  if (item.summary) return item.summary;
  return STATUS_HEADLINES[item.status];
}

/** The entry's id is the child `agentId`, so a row click can open that agent. */
export function launchedAgentToActivityEntry(item: LaunchedAgentCardItem): AgentThreadListEntry {
  return {
    id: item.agentId,
    name: item.name ?? item.agentType ?? item.model ?? item.agentId,
    turnState: item.status === 'working' || item.status === 'needs_input' ? 'working' : 'idle',
    mark: STATUS_MARKS[item.status],
    headline: headlineOf(item),
  };
}

/**
 * Emit the launched agents to the host as activity-bar rows, newest launch
 * first (cards parse in launch order; the bar and the stream-derived entries
 * it merges with lead with the most recent work). Mirrors the
 * `onActive*Change` callbacks: a change in content or in the callback's own
 * identity emits, while the fresh-but-identical arrays every stream tick
 * reparses to are suppressed by a serialized key so the host doesn't set
 * state on each tick.
 */
export function useLaunchedAgentsEmitter(
  cards: readonly LaunchedAgentCardItem[],
  onChange: ((agents: AgentThreadListEntry[]) => void) | undefined,
): void {
  const entries = useMemo(() => cards.map(launchedAgentToActivityEntry).reverse(), [cards]);
  const emittedRef = useRef<{ onChange: unknown; key: string } | null>(null);
  useEffect(() => {
    if (!onChange) return;
    const key = JSON.stringify(entries);
    const last = emittedRef.current;
    // A new callback identity re-emits even unchanged content: a remounted
    // host starts with empty state and must not miss the current set.
    if (last && last.onChange === onChange && last.key === key) return;
    emittedRef.current = { onChange, key };
    onChange(entries);
  }, [entries, onChange]);
}
